import { Button, Input, Slider } from '@/ui/components';
import { Icon } from '@/ui/icons/Icon';
import { formatAmountWithSpacesAndSymbol } from '@/lib/amountUtils';
import { useNavigate } from 'react-router-dom';
import { routes } from '@/routes/routes';
import {
  calculator,
  paymentIcon,
  reserveIcon,
  withdrawIcon,
} from '@/assets/images/emojis';
import styles from './CreditLineAmountPage.module.scss';
import { useApplication } from '../../hooks';
import { AMOUNT_SLIDER_STEP, useLoanOffer } from '@/features/offer';
import { PageLoader } from '@/layouts/components/PageLoader/PageLoader';
import { useState } from 'react';

export function CreditLineAmountPage() {
  const navigate = useNavigate();
  const { setCreditLineAmount, creditLineDraft } = useApplication();
  const { creditLineOffer, isLoading: isLoanOfferLoading } = useLoanOffer(true);
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const minAmount = creditLineOffer?.minAmount ?? 0;
  const maxAmount = creditLineOffer?.creditLimit ?? 0;
  const amount = creditLineDraft.amount ?? creditLineOffer?.creditLimit ?? 0;
  // A single possible amount: no slider, nothing to edit.
  const canChooseAmount = minAmount < maxAmount;

  const handleAmountChange = (amount: number) => {
    setCreditLineAmount(amount);
  };

  const handleSave = () => {
    navigate(routes.application.creditLineConfirmation);
  };

  const formatAmount = (amount: number) => {
    return formatAmountWithSpacesAndSymbol(amount, 'lei');
  };

  const handleEditAmount = () => {
    setIsEditingAmount(true);
    setInputValue(amount.toString());
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    setInputValue(value);
  };

  const handleInputBlur = () => {
    const numericValue = parseInt(inputValue, 10) || 0;
    const clampedValue = Math.max(minAmount, Math.min(maxAmount, numericValue));
    setCreditLineAmount(clampedValue);
    setIsEditingAmount(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  if (isLoanOfferLoading) {
    return <PageLoader />;
  }

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {/* Header Section */}
        <div className={styles.header}>
          <div className={styles.emojiContainer}>
            <img src={calculator} alt="Calculator" className={styles.emoji} />
          </div>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>Modifică limita liniei de credit</h1>
            <p className={styles.description}>
              Limită mare? Nicio grijă — plătești doar cât folosești, nu cât
              semnezi.
            </p>
          </div>
        </div>

        {/* Amount Selection Card */}
        <div className={styles.amountCard}>
          {!isEditingAmount ? (
            <div className={styles.amountHeader}>
              <div className={styles.amountInfo}>
                <p className={styles.amountLabel}>Alege suma</p>
                <div className={styles.amountValue}>
                  <span className={styles.amountText}>
                    {formatAmount(amount)}
                  </span>
                </div>
              </div>
              {canChooseAmount && (
                <div className={styles.editIcon}>
                  <Button
                    trackingId="edit_credit_line_amount"
                    iconOnly
                    size="xsmall"
                    className={styles.editButton}
                    variant="outlined"
                    leftIcon={<Icon name="pen" className={styles.editIcon} />}
                    onClick={handleEditAmount}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className={styles.amountInputContainer}>
              <Input
                type="text"
                inputMode="numeric"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                placeholder="Introdu suma"
                autoFocus
                className={styles.amountInput}
              />
            </div>
          )}

          {/* Slider Section */}
          {canChooseAmount && (
            <div className={styles.sliderSection}>
              <Slider
                value={amount}
                min={minAmount}
                max={maxAmount}
                step={AMOUNT_SLIDER_STEP}
                onChange={handleAmountChange}
                formatValue={formatAmount}
                variant="amount"
                className={styles.slider}
              />
            </div>
          )}
        </div>

        {/* Benefits Section */}
        <div className={styles.benefitsSection}>
          <div className={styles.benefitItem}>
            <div className={styles.benefitContent}>
              <h3 className={styles.benefitTitle}>
                Achiți în rate doar suma utilizată
              </h3>
              <p className={styles.benefitDescription}>
                Poți rambursa ratele la termen sau poți achita anticipat suma
                împrumutată
              </p>
            </div>
            <div className={styles.benefitIcon}>
              <img
                src={paymentIcon}
                alt="Payment"
                className={styles.iconImage}
              />
            </div>
          </div>

          <div className={styles.benefitItem}>
            <div className={styles.benefitContent}>
              <h3 className={styles.benefitTitle}>
                Ai o rezervă mereu la îndemână
              </h3>
              <p className={styles.benefitDescription}>
                Ai zero costuri pentru suma nefolosită. O poți folosi simplu
                pentru alte achiziții
              </p>
            </div>
            <div className={styles.benefitIcon}>
              <img
                src={reserveIcon}
                alt="Reserve"
                className={styles.iconImage}
              />
            </div>
          </div>

          <div className={styles.benefitItem}>
            <div className={styles.benefitContent}>
              <h3 className={styles.benefitTitle}>
                Retragi exact cât ai nevoie
              </h3>
              <p className={styles.benefitDescription}>
                Tu alegi ce sumă să ai ca și rezervă pentru achizițiile
                viitoare.
              </p>
            </div>
            <div className={styles.benefitIcon}>
              <img
                src={withdrawIcon}
                alt="Withdraw"
                className={styles.iconImage}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Button Section */}
      <div className={styles.bottomSection}>
        <Button
          trackingId="confirm_credit_line_amount"
          onClick={handleSave}
          className={styles.saveButton}
          text="Salvează"
        />
      </div>
    </div>
  );
}
